import React, { useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { Download, Copy, Check, Minimize2, Globe, ExternalLink, Lock } from 'lucide-react';
import { slugify } from '../utils/stringUtils.js';

interface BarcodeViewerProps {
  uibn: string;
  bookTitle?: string;
  className?: string;
  showDownload?: boolean;
  defaultDensity?: 'compact' | 'standard' | 'large';
  onSwitchToQr?: () => void;
}

export const BarcodeViewer: React.FC<BarcodeViewerProps> = ({
  uibn,
  bookTitle,
  className = '',
  showDownload = true,
  defaultDensity = 'compact',
  onSwitchToQr,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [copied, setCopied] = useState(false);
  const [encodeMode, setEncodeMode] = useState<'compact-code' | 'serial-only' | 'uibn' | 'url'>('uibn');
  const [density, setDensity] = useState<'compact' | 'standard' | 'large'>(defaultDensity);

  const slug = bookTitle ? slugify(bookTitle) : uibn;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.web.id';
  const directUrl = `${origin}/buku/${encodeURIComponent(uibn)}`;

  // Formats:
  // 1. compactUibn: Drop 'UIBN-' prefix (saves 5 characters, shrinks width by 30%)
  const compactUibn = uibn.replace(/^UIBN-/i, '');

  // 2. serialDigits: Pure numeric code (Code 128 Set C pairs digits, shrinking width by ~50%)
  const serialDigits = uibn.replace(/\D/g, '') || '00000001';

  let barcodeValue = uibn;
  let displayValueText = uibn;

  if (encodeMode === 'uibn') {
    barcodeValue = uibn;
    displayValueText = uibn;
  } else if (encodeMode === 'compact-code') {
    barcodeValue = compactUibn;
    displayValueText = compactUibn;
  } else if (encodeMode === 'serial-only') {
    barcodeValue = serialDigits;
    displayValueText = serialDigits;
  } else if (encodeMode === 'url') {
    barcodeValue = directUrl;
    displayValueText = directUrl;
  }

  // Sizing configurations based on density with enlarged, highly readable typography
  const config = {
    compact: {
      width: encodeMode === 'url' ? 1.15 : (encodeMode === 'serial-only' ? 1.35 : 1.25),
      height: 38,
      fontSize: 13,
      margin: 4,
      approxMm: encodeMode === 'serial-only' ? '± 30 x 14 mm' : '± 38 x 15 mm',
    },
    standard: {
      width: encodeMode === 'url' ? 1.35 : (encodeMode === 'serial-only' ? 1.55 : 1.45),
      height: 48,
      fontSize: 15,
      margin: 6,
      approxMm: encodeMode === 'serial-only' ? '± 38 x 17 mm' : '± 50 x 18 mm',
    },
    large: {
      width: encodeMode === 'url' ? 1.55 : 1.75,
      height: 56,
      fontSize: 17,
      margin: 7,
      approxMm: '± 62 x 21 mm',
    },
  }[density];

  // Render SVG barcode in DOM with Left: Code & Right: uibn.web.id
  useEffect(() => {
    if (!svgRef.current || !barcodeValue) return;

    try {
      const svg = svgRef.current;
      // Clear previous injected child elements
      while (svg.firstChild) {
        svg.removeChild(svg.firstChild);
      }

      // Render barcode lines without default text
      JsBarcode(svg, barcodeValue, {
        format: 'CODE128',
        lineColor: '#0f172a',
        width: config.width,
        height: config.height,
        displayValue: false,
        margin: config.margin,
        background: '#ffffff',
      });

      const currentHeight = parseFloat(svg.getAttribute('height') || `${config.height + config.margin * 2}`);
      const currentWidth = parseFloat(svg.getAttribute('width') || '220');

      // Add generous spacing for text height + bottom margin to prevent descenders clipping in vector SVG downloads
      const textPadding = 10;
      const textSpacing = Math.round(config.fontSize + textPadding + 4);
      const newTotalHeight = Math.round(currentHeight + textSpacing);

      // CRITICAL FIX: Must update BOTH height AND viewBox so SVG vector renderers/downloaders do NOT crop the bottom text!
      svg.setAttribute('height', `${newTotalHeight}`);
      svg.setAttribute('viewBox', `0 0 ${currentWidth} ${newTotalHeight}`);

      const textY = Math.round(currentHeight + config.fontSize - 1);

      // 1. Left/Center Text: UIBN Code or Serial Number (Enlarged & Bold Monospace)
      const leftText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      if (encodeMode === 'serial-only') {
        leftText.setAttribute('x', `${currentWidth / 2}`);
        leftText.setAttribute('text-anchor', 'middle');
      } else {
        leftText.setAttribute('x', `${config.margin + 1}`);
        leftText.setAttribute('text-anchor', 'start');
      }
      leftText.setAttribute('y', `${textY}`);
      leftText.setAttribute('fill', '#0f172a');
      leftText.setAttribute('font-family', '"JetBrains Mono", monospace');
      leftText.setAttribute('font-weight', '800');
      leftText.setAttribute('font-size', `${config.fontSize}px`);
      leftText.textContent = displayValueText;
      svg.appendChild(leftText);

      // 2. Right Text: uibn.web.id (Omitted for serial-only super compact format)
      if (encodeMode !== 'serial-only') {
        const rightText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        rightText.setAttribute('x', `${currentWidth - config.margin - 1}`);
        rightText.setAttribute('y', `${textY}`);
        rightText.setAttribute('text-anchor', 'end');
        rightText.setAttribute('fill', '#4f46e5');
        rightText.setAttribute('font-family', '"Inter", -apple-system, BlinkMacSystemFont, sans-serif');
        rightText.setAttribute('font-weight', '800');
        rightText.setAttribute('font-size', `${Math.max(11, config.fontSize * 0.95)}px`);
        rightText.textContent = 'uibn.web.id';
        svg.appendChild(rightText);
      }

    } catch {
      // Safe fallback
      try {
        if (svgRef.current) {
          JsBarcode(svgRef.current, serialDigits, {
            format: 'CODE128',
            width: 1.3,
            height: 36,
            displayValue: true,
            text: `${uibn}   uibn.web.id`,
            background: '#ffffff',
          });
        }
      } catch (e) {
        console.error('Barcode render fallback error:', e);
      }
    }
  }, [barcodeValue, displayValueText, density, encodeMode, config, serialDigits, uibn]);

  // Clean vector SVG download
  const downloadSvg = () => {
    if (!svgRef.current) return;
    try {
      let svgData = new XMLSerializer().serializeToString(svgRef.current);
      
      // Ensure SVG has proper XML namespaces for Illustrator/CorelDraw/Browsers
      if (!svgData.includes('xmlns="http://www.w3.org/2000/svg"')) {
        svgData = svgData.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ');
      }
      
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `barcode-uibn-${encodeMode}-${slug || uibn}.svg`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download SVG barcode:', err);
    }
  };

  // Master High-Resolution PNG download (300/600 DPI Print Quality)
  // Layout: Barcode lines on top; in the text row below: Left = Code, Right (mepet kanan) = uibn.web.id (Extra Large & Bold)
  const downloadPng = () => {
    try {
      const scale = 16; // 16x crisp supersampling (~4000px wide master PNG, perfect for 600-1200 DPI offset printing)
      const canvas = document.createElement('canvas');

      const barcodeBarsCanvas = document.createElement('canvas');
      JsBarcode(barcodeBarsCanvas, barcodeValue, {
        format: 'CODE128',
        lineColor: '#000000', // Pure black lines for perfect printing ink absorption
        width: Math.max(3.2, config.width * scale),
        height: Math.max(160, config.height * scale),
        displayValue: false,
        margin: config.margin * scale,
        background: '#ffffff',
      });

      const textSpacing = Math.round(52 * (scale / 4));
      canvas.width = barcodeBarsCanvas.width;
      canvas.height = barcodeBarsCanvas.height + textSpacing;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Fill crisp white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw barcode stripes
      ctx.drawImage(barcodeBarsCanvas, 0, 0);

      const textY = barcodeBarsCanvas.height + Math.round(textSpacing * 0.44);
      const fontSize = Math.round(32 * (scale / 4));
      const marginX = (config.margin * scale) + Math.round(scale * 1.5);

      // 1. Draw Code (Center for serial-only, Left for others)
      ctx.fillStyle = '#000000'; // Pure black text for high legibility
      ctx.font = `800 ${fontSize}px "JetBrains Mono", monospace`;
      ctx.textBaseline = 'middle';

      if (encodeMode === 'serial-only') {
        ctx.textAlign = 'center';
        ctx.fillText(displayValueText, canvas.width / 2, textY);
      } else {
        ctx.textAlign = 'left';
        ctx.fillText(displayValueText, marginX, textY);

        // 2. Draw Right: uibn.web.id (Only for non-serial-only modes)
        ctx.fillStyle = '#4f46e5';
        ctx.font = `800 ${Math.round(fontSize * 0.94)}px "Inter", "Plus Jakarta Sans", -apple-system, sans-serif`;
        ctx.textAlign = 'right';
        ctx.fillText('uibn.web.id', canvas.width - marginX, textY);
      }

      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `barcode-ultra-hd-600dpi-${encodeMode}-${slug || uibn}.png`;
      link.click();
    } catch (err) {
      console.error('Error generating PNG barcode:', err);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(barcodeValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div id={`barcode-container-${uibn}`} className={`flex flex-col items-center bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs ${className}`}>
      {/* Sizing & Content Controls */}
      <div className="w-full space-y-2 mb-2 pb-2.5 border-b border-slate-100 text-[11px]">
        {/* Row 1: Content Format Selection */}
        <div className="flex items-center justify-between gap-1 flex-wrap">
          <span className="text-slate-600 font-bold flex items-center gap-1">
            <Minimize2 className="w-3 h-3 text-indigo-600" />
            <span>Format Teks:</span>
          </span>
          <div className="flex bg-slate-100 p-0.5 rounded-lg font-medium">
            <button
              type="button"
              onClick={() => setEncodeMode('compact-code')}
              className={`px-2 py-0.5 rounded-md transition cursor-pointer text-[10.5px] ${encodeMode === 'compact-code' ? 'bg-white text-indigo-700 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              title="UIBN Ringkas tanpa prefix UIBN- (Menghemat ruang horizontal 30%)"
            >
              Ringkas (Hemat Ruang)
            </button>
            <button
              type="button"
              onClick={() => setEncodeMode('serial-only')}
              className={`px-2 py-0.5 rounded-md transition cursor-pointer text-[10.5px] ${encodeMode === 'serial-only' ? 'bg-white text-emerald-700 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              title="Hanya Angka Seri (Super Kompak 50% lebih pendek via Code128 Set C)"
            >
              Angka Seri (Super Kompak)
            </button>
            <button
              type="button"
              onClick={() => setEncodeMode('uibn')}
              className={`px-2 py-0.5 rounded-md transition cursor-pointer text-[10.5px] ${encodeMode === 'uibn' ? 'bg-white text-indigo-700 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              title="Kode lengkap UIBN-..."
            >
              UIBN Penuh
            </button>
            {bookTitle && (
              <button
                type="button"
                onClick={() => setEncodeMode('url')}
                className={`px-2 py-0.5 rounded-md transition cursor-pointer text-[10.5px] ${encodeMode === 'url' ? 'bg-white text-indigo-700 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
                title="Tautan /buku"
              >
                URL Buku
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Density / Width Size Selection */}
        <div className="flex items-center justify-between gap-1 flex-wrap">
          <div className="flex items-center gap-1.5 text-slate-500">
            <span className="font-medium">Ukuran Cetak:</span>
            <span className="text-[10px] text-emerald-700 font-mono bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 font-semibold">{config.approxMm}</span>
          </div>
          <div className="flex bg-slate-100 p-0.5 rounded-lg font-medium text-[10px]">
            <button
              type="button"
              onClick={() => setDensity('compact')}
              className={`px-2 py-0.5 rounded-md transition cursor-pointer ${density === 'compact' ? 'bg-emerald-600 text-white shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              title="Ukuran Ramping / Hemat Ruang (Paling disarankan untuk sampul belakang & punggung buku)"
            >
              Ramping (S)
            </button>
            <button
              type="button"
              onClick={() => setDensity('standard')}
              className={`px-2 py-0.5 rounded-md transition cursor-pointer ${density === 'standard' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              title="Ukuran Standar"
            >
              Sedang (M)
            </button>
            <button
              type="button"
              onClick={() => setDensity('large')}
              className={`px-2 py-0.5 rounded-md transition cursor-pointer ${density === 'large' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'text-slate-600 hover:text-slate-900'}`}
              title="Ukuran Lebar"
            >
              Lebar (L)
            </button>
          </div>
        </div>
      </div>

      {/* Barcode SVG Rendering Container */}
      <div className={`overflow-x-auto max-w-full flex justify-center py-1.5 px-2 bg-slate-50/50 rounded-lg w-full ${!showDownload ? 'select-none' : ''}`}>
        <svg
          ref={svgRef}
          className={`max-w-full h-auto ${!showDownload ? 'pointer-events-none select-none' : ''}`}
          onContextMenu={!showDownload ? (e) => e.preventDefault() : undefined}
        />
      </div>

      {/* Direct Website Info Badge */}
      <div className="w-full mt-2 px-2.5 py-1.5 bg-indigo-50/80 rounded-lg border border-indigo-100 flex items-center justify-between text-[10.5px]">
        <div className="flex items-center gap-1.5 text-indigo-900 font-medium">
          <Globe className="w-3.5 h-3.5 text-indigo-600" />
          <a
            href="https://uibn.web.id"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono font-bold text-indigo-700 hover:underline flex items-center gap-0.5"
          >
            <span>uibn.web.id</span>
            <ExternalLink className="w-2.5 h-2.5 opacity-70" />
          </a>
        </div>
        <span className="font-mono text-emerald-700 bg-emerald-100/70 px-1.5 py-0.5 rounded text-[9.5px] font-semibold">
          300 DPI HD
        </span>
      </div>

      {showDownload ? (
        <div className="mt-2.5 flex flex-col sm:flex-row items-center gap-2 pt-2 border-t border-slate-100 w-full justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1.5 text-[11px] truncate max-w-full sm:max-w-[180px]">
            <span className="font-mono font-bold text-slate-800 truncate" title={barcodeValue}>
              {displayValueText}
            </span>
            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1 rounded font-sans font-medium shrink-0">
              Siap Cetak
            </span>
          </div>

          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            <button
              id={`btn-copy-uibn-${uibn}`}
              type="button"
              onClick={copyCode}
              className="px-2 py-1 flex items-center gap-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition cursor-pointer text-[11px]"
              title="Salin Kode Barcode"
            >
              {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
              <span>{copied ? 'Tersalin' : 'Salin'}</span>
            </button>

            <button
              id={`btn-download-barcode-svg-${uibn}`}
              type="button"
              onClick={downloadSvg}
              className="px-2.5 py-1 flex items-center gap-1 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 transition font-semibold cursor-pointer border border-indigo-200 text-[11px]"
              title="Unduh Barcode Format SVG Vektor (Termasuk nomor & uibn.web.id di kanan)"
            >
              <Download className="w-3 h-3" />
              <span>Unduh SVG</span>
            </button>

            <button
              id={`btn-download-barcode-png-${uibn}`}
              type="button"
              onClick={downloadPng}
              className="px-2.5 py-1 flex items-center gap-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white transition font-semibold cursor-pointer shadow-2xs text-[11px]"
              title="Unduh Barcode Format PNG Resolusi Tinggi (Termasuk nomor & uibn.web.id di kanan)"
            >
              <Download className="w-3 h-3" />
              <span>Unduh PNG</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2.5 w-full pt-2 border-t border-slate-100 text-center">
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-medium">
            <Lock className="w-3 h-3 text-slate-400" />
            <span>Pratinjau Publik (Unduh/Salin Terproteksi)</span>
          </span>
        </div>
      )}

      {/* Helpful hint for book publishers */}
      <div className="mt-2 text-[10px] text-slate-500 w-full flex items-center justify-between gap-1 pt-1.5 border-t border-slate-100">
        <span>💡 Nomor di kiri dan domain <b>uibn.web.id</b> di kanan rapi sejajar.</span>
        {onSwitchToQr && (
          <button
            type="button"
            onClick={onSwitchToQr}
            className="text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer underline text-[10px] shrink-0"
          >
            Gunakan QR Kotak
          </button>
        )}
      </div>
    </div>
  );
};
